import { Fragment, useCallback, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { GICSItemRow } from './GICSItemRow';
import { FilterExcludedCountBadge } from '../FilterExcludedCountBadge';
import { FilterResetButton } from '../FilterResetButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Industry, Sector, SubIndustry } from '@biasafe.models';
import { cn } from '@/lib/utils';
import { CLASSIFICATION_STATUS } from '../../../config/constants/staticClassifications';

const TABS = ['sectors', 'industryGroups', 'industries'] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  sectors: 'Sectors',
  industryGroups: 'Industry Groups',
  industries: 'Industries',
};

// Cross-tab search mixes Sectors / Industry Groups / Industries in one list, so
// the row label must come from each item's own type — not the active tab.
const TYPE_LABEL_BY_TYPE: Record<
  'Sector' | 'Industry' | 'Sub-Industry',
  string
> = {
  Sector: 'Sector',
  Industry: 'Industry Group',
  'Sub-Industry': 'Industry',
};

function humanizeSuperSector(superSector: string): string {
  return superSector.charAt(0).toUpperCase() + superSector.slice(1);
}

// Lower = more recent, so it wins as the representative code.
const STATUS_RANK: Record<string, number> = {
  current: 0,
  pending2026: 1,
  retired: 2,
};
function statusRank(code: string): number {
  return STATUS_RANK[CLASSIFICATION_STATUS[code] ?? ''] ?? 3;
}

// Unify the identity string so a classification renumbered with only a
// punctuation difference still collapses. Real case: the catalog carries
// "Beverages—Non-Alcoholic" (em dash) and "Beverages–Non-Alcoholic" (en dash)
// as two codes for the same group — exact string equality misses it. We only
// fold dash glyphs + case + whitespace, NOT words, so genuinely different names
// (e.g. "…Apparel & Furniture" vs "…Apparel & Accessories") stay distinct.
const DASH_GLYPHS = /[‐-―−]/g;
function normalizeIdentity(value: string | undefined): string {
  // Tolerate a missing label (like the old string-template key did) instead of
  // throwing and taking the whole picker down with it.
  return (value ?? '')
    .replace(DASH_GLYPHS, '-')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export interface GICSDisplayItem {
  /** Representative (most-recent) code — the React key + partial-state lookup. */
  code: string;
  /** EVERY code this row stands for (representative + renumbered aliases). */
  codes: Array<string>;
  type: 'Sector' | 'Industry' | 'Sub-Industry';
  name: string;
  subtitle: string;
}

/**
 * Collapse rows that are the SAME classification under a different code.
 *
 * The static catalog is a point-in-time UNION, so a classification renumbered
 * across GECS vintages appears once per code (e.g. Asset Management `10310`
 * current + `10319` retired, same parent sector). Showing both is redundant —
 * identical name, identical category, only the code differs. We render ONE row
 * (the most-recent code by CLASSIFICATION_STATUS) but keep EVERY code, so
 * excluding it excludes all of them, not just the displayed one.
 *
 * Identity is name + subtitle: the subtitle encodes the full parent path
 * (cyclicality • sector [• industry group]), so a name reused under a DIFFERENT
 * parent stays its own row — those are genuinely different classifications.
 */
export function collapseDuplicates(
  items: ReadonlyArray<Omit<GICSDisplayItem, 'codes'>>,
): Array<GICSDisplayItem> {
  const groups = new Map<string, Array<Omit<GICSDisplayItem, 'codes'>>>();
  for (const item of items) {
    const key = `${normalizeIdentity(item.name)}\u0000${normalizeIdentity(item.subtitle)}`;
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }
  return Array.from(groups.values(), (group) => {
    const rep = group.reduce((best, cur) =>
      statusRank(cur.code) < statusRank(best.code) ? cur : best,
    );
    return { ...rep, codes: group.map((g) => g.code) };
  });
}

interface GICSClassificationsPanelProps {
  excluded: Set<string>;
  /** Codes disabled by the Cyclicality selector — visible, not selectable. */
  disabledCodes?: Set<string>;
  /** Codes the selected universe holds constituents for today. */
  liveCodes?: Set<string>;
  onToggle: (codes: Array<string>) => void;
  sectors: ReadonlyArray<Sector>;
  industries: ReadonlyArray<Industry>;
  subIndustries: ReadonlyArray<SubIndustry>;
  onSwitchToSimple: () => void;
  onReset?: () => void;
  /** Securities dropped by this filter — same badge as the simple header. */
  excludedSecurityCount?: number;
}

export function GICSClassificationsPanel({
  excluded,
  disabledCodes,
  liveCodes,
  onToggle,
  sectors,
  industries,
  subIndustries,
  onSwitchToSimple,
  onReset,
  excludedSecurityCount = 0,
}: GICSClassificationsPanelProps) {
  const [activeTab, setActiveTab] = useState<Tab>('sectors');
  const [search, setSearch] = useState('');

  const getItems = useCallback(
    (tab: Tab) => {
      switch (tab) {
        case 'sectors':
          return collapseDuplicates(
            sectors.map((s) => ({
              code: s.code,
              type: 'Sector' as const,
              name: s.label,
              subtitle: humanizeSuperSector(s.superSector),
            })),
          );
        case 'industryGroups':
          return collapseDuplicates(
            industries.map((i) => {
              const parentSector = sectors.find((s) => s.code === i.sectorCode);
              // Full path: Cyclicality › Sector.
              const cyc = parentSector
                ? `${humanizeSuperSector(parentSector.superSector)} • `
                : '';
              return {
                code: i.code,
                type: 'Industry' as const,
                name: i.label,
                subtitle: `${cyc}${parentSector?.label ?? i.sectorCode}`,
              };
            }),
          );
        case 'industries':
          return collapseDuplicates(
            subIndustries.map((si) => {
              const parentSector = sectors.find(
                (s) => s.code === si.sectorCode,
              );
              const parentIndustry = industries.find(
                (ind) => ind.code === si.industryCode,
              );
              // Full path: Cyclicality \u203a Sector \u203a Industry Group.
              const cyc = parentSector
                ? `${humanizeSuperSector(parentSector.superSector)} \u2022 `
                : '';
              return {
                code: si.code,
                type: 'Sub-Industry' as const,
                name: si.label,
                subtitle: `${cyc}${parentSector?.label ?? si.sectorCode} \u2022 ${parentIndustry?.label ?? si.industryCode}`,
              };
            }),
          );
      }
    },
    [sectors, industries, subIndustries],
  );

  // While searching, look across ALL categories (sectors + industry groups +
  // industries), not just the active tab — matching the Figma's flat search.
  // With no query, show the active tab's list.
  const items = useMemo(() => {
    if (search.trim()) {
      return [
        ...getItems('sectors'),
        ...getItems('industryGroups'),
        ...getItems('industries'),
      ];
    }
    return getItems(activeTab);
  }, [search, activeTab, getItems]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.codes.some((c) => c.includes(q)) ||
        item.subtitle.toLowerCase().includes(q),
    );
  }, [items, search]);

  // A parent is "partially excluded" when it isn't excluded outright but
  // some of its children are — the Figma amber "Partial" state. Computed
  // from the real exclusion set + the real GICS hierarchy.
  // Partial-parent set across ALL levels (not just the active tab) so it's
  // correct whether the list shows one tab or cross-tab search results.
  const partialCodes = useMemo(() => {
    const set = new Set<string>();
    for (const s of sectors) {
      if (excluded.has(s.code)) continue;
      const childExcluded =
        industries.some(
          (i) => i.sectorCode === s.code && excluded.has(i.code),
        ) ||
        subIndustries.some(
          (si) => si.sectorCode === s.code && excluded.has(si.code),
        );
      if (childExcluded) set.add(s.code);
    }
    for (const i of industries) {
      if (excluded.has(i.code)) continue;
      if (
        subIndustries.some(
          (si) => si.industryCode === i.code && excluded.has(si.code),
        )
      ) {
        set.add(i.code);
      }
    }
    return set;
  }, [excluded, sectors, industries, subIndustries]);

  // Counts reflect the DEDUPED rows the user actually sees, not the raw
  // point-in-time union (which double-counts renumbered classifications).
  const tabCounts: Record<Tab, number> = {
    sectors: getItems('sectors').length,
    industryGroups: getItems('industryGroups').length,
    industries: getItems('industries').length,
  };

  return (
    <div className="space-y-2">
      {/* Header — Reset right of the title (Figma), Simple View toggle right. */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[15px] font-medium text-muted-foreground truncate">
            Sectors, Industry Groups, & Industries
          </span>
          {excluded.size > 0 && onReset && (
            <FilterResetButton onClick={onReset} />
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {excluded.size > 0 && (
            <FilterExcludedCountBadge count={excludedSecurityCount} />
          )}
          <button
            type="button"
            onClick={onSwitchToSimple}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Simple View
          </button>
        </div>
      </div>

      {/* Panel — Figma caps the height on the scrollable list only, so the
          search header + tabs stay pinned and the list gets the full max-h-72
          of rows (not panel-height-minus-chrome). */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {/* Search — Figma: labeled header on a muted wash, bordered
            Input with the icon nested inside. */}
        <div className="border-b bg-muted/30 px-4 py-3">
          {/* Title and search share ONE row: the search takes the right
              half of the header band. Stacked, the two claimed a header
              twice as tall as it needed to be and pushed the tabs — the
              part people actually reach for — further down the panel. */}
          <div className="flex items-center gap-3">
            <Label className="text-[15px] flex-1 min-w-0 truncate">
              Business Classifications
            </Label>
            <div className="relative w-1/2 shrink-0">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search sectors, industry groups or industries..."
                className="pl-7 h-7 w-full text-xs border-border/75"
              />
            </div>
          </div>
        </div>

        {/* Tabs — Figma: flex with thin dividers, active tab gets a
            bg-primary/10 wash + a 2px bottom underline bar. */}
        <div className="sticky top-0 z-10 flex items-center gap-0 border-b border-border/50 bg-background/15 backdrop-blur-md">
          {TABS.map((tab, idx) => (
            <Fragment key={tab}>
              {idx > 0 && <div className="w-px h-4 bg-border/75 shrink-0" />}
              <button
                type="button"
                onClick={() => {
                  setActiveTab(tab);
                  setSearch('');
                }}
                className={cn(
                  'flex-1 relative px-3 py-2 text-[14px] font-medium transition-all flex items-center justify-center gap-1.5',
                  activeTab === tab
                    ? 'text-foreground bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {TAB_LABELS[tab]}
                <span
                  className={cn(
                    'text-[12px]',
                    activeTab === tab ? 'opacity-80' : 'opacity-50',
                  )}
                >
                  ({tabCounts[tab]})
                </span>
                {activeTab === tab && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary rounded-t-full" />
                )}
              </button>
            </Fragment>
          ))}
        </div>

        {/* List */}
        <div className="max-h-72 overflow-y-auto divide-y divide-border/40">
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No results found.
            </p>
          ) : (
            filtered.map((item) => (
              <GICSItemRow
                key={item.code}
                code={item.code}
                type={item.type}
                typeLabel={TYPE_LABEL_BY_TYPE[item.type]}
                name={item.name}
                subtitle={item.subtitle}
                // A collapsed row stands for every alias code, so it reads as
                // excluded / disabled / live if ANY of them is, and toggling it
                // flips them all together.
                isExcluded={item.codes.some((c) => excluded.has(c))}
                isPartiallyExcluded={partialCodes.has(item.code)}
                isDisabled={item.codes.some((c) => !!disabledCodes?.has(c))}
                isLive={item.codes.some((c) => !!liveCodes?.has(c))}
                onClick={() => onToggle(item.codes)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
